import type { Condition } from '../../scranner/types';
import type { QueryClient } from '@tanstack/query-core';
import type { ScreenerPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import { IdeaDTO } from 'src/dtos/Idea';
import type { IdeaPlanToStageOptions } from 'src/dtos/Idea';
import { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import { IndicatorImportModel } from 'src/dtos/IndicatorImport';
import {
    createGuidedConditionModel,
    SCRANNER_CHANGE_EVENT,
    ScrannerPlanDTO,
} from 'src/features/scranner/dtos/ScrannerPlanDTO';
import type { ScrannerChangeEvent } from 'src/features/scranner/dtos/ScrannerPlanDTO';
import { ConditionGroupModel } from 'src/features/searchAlpha/dtos/ConditionGroupModel';
import { conditionGroupModelToIfThenGroup } from 'src/features/searchAlpha/dtos/ConditionGroupModel/conditionGroupModelToIfThenGroup';
import { FilterStage, SourceStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import type { SlugMapService } from 'src/services/SlugMapService';
import { container } from 'src/StaticContainer';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';

const log = container.get('Logger').getSubLogger({ name: 'feat:screener:ScreenerPlanDTO' });

interface ScreenerPlanDTOInitializer {
    isDirty: boolean;
    conditions: Condition[];
    excludeList: string[];
    includeList: string[];
    mode: 'exclude' | 'include-any' | 'include-all';
}

interface ScreenerPlanDTOState {
    excludeList: string[];
    includeList: string[];
}

export class ScreenerPlanDTO extends ScrannerPlanDTO {
    get excludeList() {
        return this.#state.excludeList;
    }

    get includeList() {
        return this.#state.includeList;
    }

    constructor(
        state: ScreenerPlanDTOInitializer | undefined,
        queryClient: QueryClient,
        formulaService: ReactBindings['FormulaService'],
        slugMapService: SlugMapService,
    ) {
        super(state, queryClient, formulaService);

        this.#queryClient = queryClient;
        this.#formulaService = formulaService;
        this.#slugMapService = slugMapService;

        this.#state = state ?? {
            excludeList: [],
            includeList: [],
        };
    }

    #formulaService: ReactBindings['FormulaService'];
    #slugMapService: SlugMapService;

    override toContract(): ScreenerPlan {
        return {
            ...this.toScrannerContract(),
            type: 'screener',
            excludeList: this.#state.excludeList,
            includeList: this.#state.includeList,
        };
    }

    static async fromPlan(
        plan: ScreenerPlan,
        queryClient: ReactBindings['QueryClient'],
        formulaService: ReactBindings['FormulaService'],
        slugMapService: SlugMapService,
        options?: { isDirty?: boolean },
    ): Promise<ScreenerPlanDTO> {
        return new ScreenerPlanDTO(
            {
                ...(await ScrannerPlanDTO.fromPlanToInitializer(plan, queryClient, formulaService)),
                isDirty: options?.isDirty ?? false,
                excludeList: plan.excludeList,
                includeList: plan.includeList,
            },
            queryClient,
            formulaService,
            slugMapService,
        );
    }

    setExcludeList(listOrFn: string[] | ((currentList: string[]) => string[])) {
        const excludeList = typeof listOrFn === 'function' ? listOrFn(this.#state.excludeList) : listOrFn;
        const includeList = this.#state.includeList.filter((item) => !excludeList.includes(item));

        this.isDirty = true;

        this.state = {
            ...this.#state,
            excludeList,
            includeList,
        };
    }

    setIncludeList(listOrFn: string[] | ((currentList: string[]) => string[])) {
        const includeList = typeof listOrFn === 'function' ? listOrFn(this.#state.includeList) : listOrFn;
        const excludeList = this.#state.excludeList.filter((item) => !includeList.includes(item));

        this.isDirty = true;
        this.state = {
            ...this.#state,
            includeList,
            excludeList,
        };
    }

    #queryClient: QueryClient;

    #state: ScreenerPlanDTOState;

    private set state(newState: ScreenerPlanDTOState) {
        this.#state = newState;
        this.changeTarget.dispatchEvent(new CustomEvent(SCRANNER_CHANGE_EVENT) satisfies ScrannerChangeEvent);
    }

    async toSearchPlan(): Promise<SearchPlanDTO> {
        if (
            !this.conditions.some((x) => x.enabled) &&
            this.#state.includeList.length === 0 &&
            this.#state.excludeList.length === 0
        ) {
            return SearchPlanDTO.fromPlanAndImports(
                {
                    type: 'if-then',
                    root: {
                        type: 'group',
                        name: '',
                        collapsed: false,
                        enabled: true,
                        lines: [
                            {
                                type: 'line',
                                enabled: true,
                                id: v4(),
                                mode: 'formula',
                                formula: this.mode === 'exclude' ? 'true == true' : 'true == false',
                                description: '',
                                fieldInView: 'formula',
                            },
                        ],
                        id: v4(),
                        description: '',
                        operator: 'and',
                    },
                    imports: [],
                },
                [],
            );
        }

        await this.#slugMapService.initialize();
        const slugMap = this.#slugMapService.slugMap.value;

        const imports: IndicatorImportModel[] = [];

        const symbolIndicator = slugMap['symbol'];
        if (!symbolIndicator) {
            log.error('Slug map missing canonical "symbol" indicator', { slugMap });
            throw new Error('Slug map missing canonical "symbol" indicator');
        }
        const symbolIndicatorImportModel = IndicatorImportModel.fromIndicatorAndImports(symbolIndicator, imports);

        let inclusions: ConditionGroupModel | undefined;
        if (this.#state.includeList.length > 0) {
            if (!imports.includes(symbolIndicatorImportModel)) {
                imports.push(symbolIndicatorImportModel);
            }

            inclusions = new ConditionGroupModel({
                id: v4(),
                label: 'Symbol Inclusions',
                children: [],
                booleanOperator: 'or',
                isExpanded: true,
                isSelfEnabled: true,
                parent: false,
            });

            for (const symbol of this.#state.includeList) {
                createGuidedConditionModel(inclusions, symbolIndicatorImportModel.alias, '==', `"${symbol}"`);
            }
        }

        let exclusions: ConditionGroupModel | undefined;
        if (this.#state.excludeList.length > 0) {
            if (!imports.includes(symbolIndicatorImportModel)) {
                imports.push(symbolIndicatorImportModel);
            }

            exclusions = new ConditionGroupModel({
                id: v4(),
                label: 'Symbol Exclusions',
                children: [],
                booleanOperator: 'and',
                isExpanded: true,
                isSelfEnabled: true,
                parent: false,
            });

            for (const symbol of this.#state.excludeList) {
                createGuidedConditionModel(exclusions, symbolIndicatorImportModel.alias, '!=', `"${symbol}"`);
            }
        }

        let conditions: ConditionGroupModel | undefined;
        // eslint-disable-next-line prefer-const -- keeping the same pattern as the inclusion/exclusion lists
        conditions = await this.toConditionGroupModel(imports);

        if (!inclusions && !exclusions && conditions) {
            return SearchPlanDTO.fromPlanAndImports(
                {
                    type: 'if-then',
                    root: conditionGroupModelToIfThenGroup(conditions),
                    imports: imports.values().toArray(),
                },
                imports,
            );
        }

        const rootGroup = new ConditionGroupModel({
            id: v4(),
            label: 'Root',
            children: [],
            booleanOperator: this.mode !== 'exclude' && !exclusions ? 'or' : 'and',
            isExpanded: true,
            isSelfEnabled: true,
            parent: false,
        });

        let middleGroup;
        if (inclusions && exclusions && conditions) {
            middleGroup = new ConditionGroupModel({
                id: v4(),
                label: this.mode === 'exclude' ? 'Exclusions' : 'Inclusions',
                children: [],
                booleanOperator: this.mode === 'exclude' ? 'and' : 'or',
                isExpanded: true,
                isSelfEnabled: true,
                parent: false,
            });

            rootGroup.adoptChild(middleGroup);

            middleGroup.adoptChild(conditions);

            if (this.mode === 'exclude') {
                middleGroup.adoptChild(exclusions);
                rootGroup.adoptChild(inclusions);
            } else {
                middleGroup.adoptChild(inclusions);
                rootGroup.adoptChild(exclusions);
            }
        } else {
            if (conditions) rootGroup.adoptChild(conditions);
            if (inclusions) rootGroup.adoptChild(inclusions);
            if (exclusions) rootGroup.adoptChild(exclusions);
        }

        return SearchPlanDTO.fromPlanAndImports(
            {
                type: 'if-then',
                root: conditionGroupModelToIfThenGroup(rootGroup),
                imports: imports.values().toArray(),
            },
            imports,
        );
    }

    override async toStage(
        inputStage: Stage | null,
        queryClient: QueryClient,
        { validationMode, dwellTime }: IdeaPlanToStageOptions,
    ): Promise<FilterStage> {
        if (!validationMode) {
            const result = await this.renderToFormula();

            const { formula, imports } = result;

            return FilterStage.fromInputStage(
                inputStage ?? SourceStage.allForKey('ticker'),
                {
                    type: 'filter',
                    formula,
                    imports,
                    dwellTime,
                },
                [],
            );
        } else {
            const result = await this.getValidationModeFormulaAndIndicators();

            const [bespokeIndicators, formula] = result;

            return FilterStage.fromInputStage(
                inputStage ?? SourceStage.allForKey('ticker'),
                {
                    type: 'filter',
                    formula: formula.formula,
                    imports: formula.imports,
                    dwellTime,
                },
                bespokeIndicators,
            );
        }
    }

    /**
     * @deprecated - This is a temporary measure for creating runnable ideas. Long-term we should keep idea states.
     */
    toTempIdea(): IdeaDTO<ScreenerPlanDTO> {
        return IdeaDTO.fromIdeaAndPlan(
            {
                name: 'Scanner Idea',
                key: 'ticker',
                isTemplate: false,
                description: null,
                defaultUniverseId: null,
                defaultColumnTemplateId: null,
            },
            this,
        ) as IdeaDTO<ScreenerPlanDTO>;
    }

    async renderToFormula() {
        const searchPlanDTO = await this.toSearchPlan();
        return searchPlanDTO.renderToFormula()!;
    }

    async getValidationModeFormulaAndIndicators() {
        const searchPlanDTO = await this.toSearchPlan();
        return (await searchPlanDTO.getValidationModeFormulaAndIndicators())!;
    }
}
